import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * GanttBarDecisions Component
 * 
 * Renders decision buttons for GanttBar - either inline (when bar is wide enough)
 * or as a popout (when bar is narrow but selected).
 */
const GanttBarDecisions = ({
  taskId,
  options,
  answer,
  showInline,
  showPopout,
  popoutPosition,
  onAnswerChange,
  onOpenOptionsModal,
}) => {
  // Decision handlers
  const handleAnswerClick = useCallback((optionId) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    onAnswerChange?.(taskId, optionId === answer ? null : optionId);
  }, [taskId, answer, onAnswerChange]);

  const handleOpenOptionsModal = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onOpenOptionsModal?.();
  }, [onOpenOptionsModal]);

  // Inline decision buttons (inside the bar)
  if (showInline) {
    return (
      <div className="gantt-bar__decision-inline">
        {options.map((option) => (
          <button
            key={option.id}
            className={`gantt-bar__decision-btn ${answer === option.id ? 'gantt-bar__decision-btn--active' : ''}`}
            style={{ '--option-color': option.color }}
            onClick={handleAnswerClick(option.id)}
            onMouseDown={(e) => e.stopPropagation()}
            title={option.label}
          >
            {option.label}
          </button>
        ))}
        <button
          className="gantt-bar__decision-add-btn"
          onClick={handleOpenOptionsModal}
          onMouseDown={(e) => e.stopPropagation()}
          title="Add or edit choices"
        >
          +
        </button>
      </div>
    );
  }

  // Popout decision buttons (rendered via portal)
  if (showPopout && popoutPosition) {
    return createPortal(
      <div 
        className="gantt-bar__decision-popout gantt-bar__decision-popout--portal"
        style={{
          position: 'fixed',
          top: popoutPosition.top,
          left: popoutPosition.left,
          transform: 'translateX(-50%)',
        }}
      >
        {options.map((option) => (
          <button
            key={option.id}
            className={`gantt-bar__decision-btn ${answer === option.id ? 'gantt-bar__decision-btn--active' : ''}`}
            style={{ '--option-color': option.color }}
            onClick={handleAnswerClick(option.id)}
            onMouseDown={(e) => e.stopPropagation()}
            title={option.label}
          >
            {option.label}
          </button>
        ))}
        <button
          className="gantt-bar__decision-add-btn"
          onClick={handleOpenOptionsModal}
          onMouseDown={(e) => e.stopPropagation()}
          title="Add or edit choices"
        >
          +
        </button>
      </div>,
      document.body
    );
  }

  return null;
};

export default GanttBarDecisions;
